// rc123 — Tarjeta de DATOS DE PAGO (CLABE SPEI) del cliente, misma linea
// visual que la tarjeta WiFi. Se dibuja 100% con datos ya cacheados en la BD
// (toku_entity_map.clabe + razon social de la empresa) — CERO llamadas a Toku.

export function formatClabe(clabe: string): string {
  if (!clabe || clabe.length !== 18) return clabe;
  return `${clabe.slice(0, 3)} ${clabe.slice(3, 6)} ${clabe.slice(6, 11)} ${clabe.slice(11, 15)} ${clabe.slice(15, 18)}`;
}

export type PagoCardData = {
  clabe: string;          // 18 digitos
  beneficiario: string;   // razon social de la empresa del cliente
  empresaNombre: string;  // nombre corto (BYTECOM / BYTELINK) para header/footer
  clienteNombre: string;
  clienteCodigo: string;
};

// Renderiza la tarjeta a un PNG (Blob), estilo espejo de renderWifiCard.
export async function renderPagoCard(d: PagoCardData): Promise<Blob> {
  const S = 2; // escala retina
  const W = 800, H = 960;
  const cv = document.createElement("canvas");
  cv.width = W * S;
  cv.height = H * S;
  const ctx = cv.getContext("2d")!;
  ctx.scale(S, S);

  const rr = (x: number, y: number, w: number, h: number, r: number) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  };
  // Encoge la fuente hasta que el texto quepa en maxW.
  const fitText = (text: string, weight: string, family: string, size: number, maxW: number) => {
    let s = size;
    ctx.font = `${weight} ${s}px ${family}`;
    while (s > 12 && ctx.measureText(text).width > maxW) {
      s -= 1;
      ctx.font = `${weight} ${s}px ${family}`;
    }
  };

  // fondo + glow (identicos a la tarjeta WiFi)
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#17142a");
  bg.addColorStop(1, "#0b0a12");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, 150, 20, W / 2, 150, 420);
  glow.addColorStop(0, "rgba(124,108,240,0.22)");
  glow.addColorStop(1, "rgba(124,108,240,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // header: logo + marca
  const lg = ctx.createLinearGradient(60, 60, 116, 116);
  lg.addColorStop(0, "#a78bfa");
  lg.addColorStop(1, "#4a9eff");
  ctx.fillStyle = lg;
  rr(60, 60, 56, 56, 16);
  ctx.fill();
  ctx.fillStyle = "#0d0c14";
  ctx.font = "800 30px Inter, sans-serif";
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText("$", 88, 90);
  ctx.textAlign = "left";
  ctx.fillStyle = "#eae7f2";
  ctx.font = "800 22px Inter, sans-serif";
  ctx.fillText("datos de pago", 130, 80);
  ctx.fillStyle = "#8b84a0";
  ctx.font = '700 12px "JetBrains Mono", monospace';
  ctx.fillText((d.empresaNombre || "PAGO").toUpperCase(), 130, 102);  // rc129: marca del caller

  // titulo
  ctx.textAlign = "center";
  ctx.fillStyle = "#ffffff";
  ctx.font = "800 34px Inter, sans-serif";
  ctx.fillText("Paga tu servicio", W / 2, 190);
  ctx.fillStyle = "#9a93ad";
  ctx.font = "500 16px Inter, sans-serif";
  ctx.fillText("Transferencia SPEI desde tu app bancaria", W / 2, 224);

  // panel blanco con los datos bancarios
  const panelX = 70, panelW = W - 140, panelY = 262, panelH = 330;
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.45)";
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 16;
  ctx.fillStyle = "#ffffff";
  rr(panelX, panelY, panelW, panelH, 28);
  ctx.fill();
  ctx.restore();

  const label = (t: string, y: number) => {
    ctx.fillStyle = "#8a8798";
    ctx.font = '700 13px "JetBrains Mono", monospace';
    ctx.fillText(t, W / 2, y);
  };

  label("BENEFICIARIO", panelY + 52);
  ctx.fillStyle = "#0d0c14";
  fitText(d.beneficiario, "800", "Inter, sans-serif", 27, panelW - 60);
  ctx.fillText(d.beneficiario, W / 2, panelY + 88);

  label("BANCO", panelY + 142);
  ctx.fillStyle = "#0d0c14";
  ctx.font = "800 24px Inter, sans-serif";
  ctx.fillText("STP — Transferencia SPEI", W / 2, panelY + 176);

  label("CLABE INTERBANCARIA", panelY + 230);
  ctx.fillStyle = "#1d3fad";
  fitText(formatClabe(d.clabe), "800", '"JetBrains Mono", monospace', 34, panelW - 60);
  ctx.fillText(formatClabe(d.clabe), W / 2, panelY + 268);

  // instruccion corta
  ctx.fillStyle = "#9a93ad";
  ctx.font = "500 16px Inter, sans-serif";
  ctx.fillText("Da de alta la CLABE en tu banca movil y transfiere tu pago.", W / 2, panelY + panelH + 52);
  ctx.fillText("Se aplica automaticamente a tu servicio.", W / 2, panelY + panelH + 80);

  // recordatorio: CLABE unica del servicio
  const remY = panelY + panelH + 116, remH = 104;
  ctx.fillStyle = "rgba(240,180,80,0.10)";
  rr(panelX, remY, panelW, remH, 18);
  ctx.fill();
  ctx.strokeStyle = "rgba(240,180,80,0.35)";
  ctx.lineWidth = 1.5;
  rr(panelX, remY, panelW, remH, 18);
  ctx.stroke();
  ctx.fillStyle = "#e8c268";
  ctx.font = "800 17px Inter, sans-serif";
  ctx.fillText("Esta CLABE es UNICA para tu servicio", W / 2, remY + 40);
  ctx.font = "500 14px Inter, sans-serif";
  ctx.fillStyle = "#cfae6d";
  ctx.fillText("No sirve para pagar otros contratos ni debe compartirse.", W / 2, remY + 70);

  // footer
  ctx.fillStyle = "#5f5975";
  ctx.font = "500 14px Inter, sans-serif";
  const quien = [d.clienteNombre, d.clienteCodigo].filter(Boolean).join(" · ");
  if (quien) ctx.fillText(quien, W / 2, H - 74);
  ctx.fillText(d.empresaNombre ? `${d.empresaNombre} · Internet` : "Internet", W / 2, H - 46);

  return await new Promise<Blob>((res) => cv.toBlob((b) => res(b!), "image/png", 0.95));
}
